import React, { useState } from "react";
import { motion } from "framer-motion";
import { Camera, Share2, MapPin, KeyRound, Music2, Check, Play } from "lucide-react";
import { BACKEND_URL } from "../../lib/api";
import { PlayerAvatar } from "./PlayerAvatar";

export const ytId = (u = "") => (/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/.exec(u) || [])[1];

export function ClipPlayer({ r }) {
  const [on, setOn] = useState(false);
  const id = ytId(r.clip_url);
  if (!id) return null;
  const start = Math.max(0, Number(r.clip_start) || 0);
  return (
    <div className="relative rounded-2xl overflow-hidden bg-black aspect-video max-w-md mb-4" data-testid={`song-clip-${r.round_id}`}>
      {on && <iframe title="Mystery clip" className="absolute inset-0 w-full h-full" src={`https://www.youtube.com/embed/${id}?autoplay=1&start=${start}&end=${start + 20}&controls=0&modestbranding=1&rel=0&playsinline=1`} allow="autoplay; encrypted-media" />}
      <div className={`absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-[#1a1a1a] to-black ${on ? "pointer-events-none" : ""}`}>
        {on ? <><Music2 className="w-10 h-10 text-[var(--gold)] animate-pulse" /><p className="text-sm text-white/70">Listen closely…</p></>
          : <button onClick={() => setOn(true)} className="btn-gold" data-testid={`song-clip-play-${r.round_id}`}><Play className="w-4 h-4" /> Play the mystery clip</button>}
      </div>
      {on && <button onClick={() => setOn(false)} className="absolute top-2 right-2 text-[11px] px-3 py-1 rounded-full bg-white/15" data-testid={`song-clip-stop-${r.round_id}`}>Stop</button>}
    </div>
  );
}

export function BrandMark({ big = false, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`} data-testid="brand-mark">
      <img src="/brand/logo-mark.png" alt="" className={big ? "h-12" : "h-7"} draggable="false" />
      <img src="/brand/logo-wordmark.png" alt="Locked In Forever" className={big ? "h-6" : "h-3.5"} draggable="false" />
    </span>
  );
}

export function WheelDisc({ segs, rot = 0, size = "w-72 h-72 md:w-80 md:h-80", label = "text-[10px]", duration = 4 }) {
  const slice = 360 / segs.length;
  const grad = segs.map((_, i) => `${i % 2 ? "#111111" : "#C8A96B"} ${i * slice}deg ${(i + 1) * slice}deg`).join(",");
  return (
    <div className={`relative ${size} mx-auto`} data-testid="wheel-disc">
      <div className="absolute left-1/2 -top-2 -translate-x-1/2 z-10 w-0 h-0 border-l-[12px] border-r-[12px] border-t-[22px] border-l-transparent border-r-transparent border-t-white drop-shadow" />
      <motion.div animate={{ rotate: rot }} transition={{ duration, ease: [0.15, 0.85, 0.25, 1] }} className="w-full h-full rounded-full ring-8 ring-[#C8A96B]/40 shadow-2xl relative" style={{ background: `conic-gradient(${grad})` }}>
        {segs.map((s, i) => (
          <span key={i} className={`absolute left-1/2 top-1/2 ${label} font-semibold text-white origin-left w-[45%] pl-[18%] truncate`} style={{ transform: `rotate(${i * slice + slice / 2 - 90}deg)` }}>{s.label}</span>
        ))}
      </motion.div>
      <span className="absolute inset-[36%] rounded-full bg-[#0b0b0b] ring-4 ring-[#C8A96B] flex items-center justify-center p-[6%]" data-testid="wheel-logo"><img src="/brand/logo-mark.png" alt="Locked In Forever" className="w-full h-full object-contain" draggable="false" /></span>
    </div>
  );
}

export const spinTo = (index, n, turns = 6) => 360 * turns + (360 - (index * (360 / n) + 180 / n));

export function ScreenWheel({ g }) {
  const last = g.screen?.last_spin;
  const segs = g.config.segments;
  return (
    <div className="grid grid-cols-[auto_1fr] gap-12 items-center" data-testid="screen-wheel">
      <WheelDisc segs={segs} rot={last ? spinTo(last.index, segs.length, 4 + (last.created_at?.length % 3 || 0)) : 0} size="w-[min(60vh,560px)] h-[min(60vh,560px)]" label="text-sm" />
      <div>
        {last ? (
          <motion.div key={last.created_at} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 4 }} data-testid="screen-wheel-last">
            <p className="text-xs uppercase tracking-[0.3em] text-white/50">Latest spin</p>
            <div className="flex items-center gap-4 mt-3"><PlayerAvatar p={last} size={56} /><p className="font-serif text-4xl">{last.name}</p></div>
            <p className="font-script text-[#C8A96B] text-4xl mt-4">landed on</p>
            <p className="font-serif text-5xl">{last.label}</p><p className="text-[#C8A96B] text-2xl mt-2">+{last.points} pts</p>
          </motion.div>
        ) : <p className="font-serif text-4xl text-white/60">Scan the code and spin the ring!</p>}
      </div>
    </div>
  );
}

export function ScreenBingo({ g }) {
  const sq = g.screen?.squares || [];
  const max = Math.max(1, ...sq.map((s) => s.count));
  const cells = [...sq.slice(0, 12), null, ...sq.slice(12, 24)];
  const brand = g.config.brand;
  return (
    <div className="grid grid-cols-[auto_1fr] gap-10 items-start" data-testid="screen-bingo">
      <div className="grid grid-cols-5 gap-2 w-[min(64vh,600px)]">
        {cells.map((s, i) => s === null ? (
          <div key="free" className="aspect-square rounded-xl bg-[#0b0b0b] ring-2 flex flex-col items-center justify-center gap-1" style={{ boxShadow: `inset 0 0 0 2px ${brand?.color || "#C8A96B"}` }} data-testid="screen-bingo-free">
            <img src={brand?.logo_url || "/brand/logo-mark.png"} alt="" className="h-1/2 object-contain" /><span className="text-[10px] tracking-[0.2em]" style={{ color: brand?.color || "#C8A96B" }}>FREE</span>
          </div>
        ) : (
          <div key={i} className="relative aspect-square rounded-xl p-1.5 text-[11px] leading-tight flex items-center justify-center text-center text-white" style={{ background: s.count ? `rgba(200,169,107,${0.25 + 0.75 * (s.count / max)})` : "rgba(255,255,255,0.06)" }}>
            {s.text}{s.count > 0 && <span className="absolute top-1 right-1 text-[10px] bg-black/60 rounded-full px-1.5">{s.count}</span>}
          </div>
        ))}
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-3">Closest to bingo</p>
        {(g.screen?.closest || []).map((p, i) => (
          <div key={p.name + i} className="flex items-center gap-3 py-2 text-2xl font-serif"><PlayerAvatar p={p} size={36} /><span className="flex-1 truncate">{p.name}</span><span className="text-white/60 text-lg">{p.marks} squares</span></div>
        ))}
      </div>
    </div>
  );
}

const HUNT_ICON = { photo: Camera, social: Share2, gps: MapPin, code: KeyRound };

export function ScreenHunt({ g }) {
  const players = (g.players || []).length || 1;
  return (
    <div className="space-y-6" data-testid="screen-hunt">
      <div className="grid grid-cols-4 xl:grid-cols-5 gap-2 max-h-[calc(100vh-420px)] overflow-hidden content-start">
        {(g.screen?.tasks || []).map((t) => {
          const Icon = HUNT_ICON[t.kind] || Camera;
          return (
            <div key={t.task_id} className="rounded-xl bg-white/5 p-2.5" data-testid={`screen-hunt-${t.task_id}`}>
              <div className="flex items-center gap-2"><Icon className="w-4 h-4 text-[#C8A96B] shrink-0" /><p className="text-sm flex-1 truncate">{t.title}</p><span className="text-[#C8A96B] text-xs">{t.points}</span></div>
              <div className="h-1 rounded-full bg-white/10 mt-2"><div className="h-full bg-[#C8A96B] rounded-full transition-[width] duration-700" style={{ width: `${Math.min(100, (t.done / players) * 100)}%` }} /></div>
              <p className="text-[11px] text-white/50 mt-1">{t.done} found it</p>
            </div>
          );
        })}
      </div>
      {g.screen?.photos?.length > 0 && <div className="flex gap-3" data-testid="screen-hunt-photos">{g.screen.photos.map((p, i) => <figure key={i} className="w-28"><img src={`${BACKEND_URL}${p.photo_url}`} alt="" className="w-28 h-28 object-cover rounded-xl" /><figcaption className="text-xs text-white/60 truncate mt-1">{p.name} · {p.task_title}</figcaption></figure>)}</div>}
    </div>
  );
}

export function ScreenSong({ g }) {
  const rounds = g.screen?.rounds || [];
  const full = g.config.rounds;
  return (
    <div className="grid grid-cols-2 gap-6" data-testid="screen-song">
      {rounds.map((r, i) => (
        <div key={r.round_id} className="rounded-2xl bg-white/5 p-5" data-testid={`screen-song-${r.round_id}`}>
          <p className="text-xs uppercase tracking-[0.3em] text-white/50 flex items-center gap-2"><Music2 className="w-4 h-4 text-[#C8A96B]" /> Round {i + 1}</p>
          <p className="font-serif text-3xl mt-1 mb-3">{r.prompt}</p>
          {full[i] && <ClipPlayer r={full[i]} />}
          <div className="grid grid-cols-2 gap-2">{full[i]?.options.map((o, k) => <div key={k} className="rounded-xl bg-white/10 px-3 py-2 text-lg">{o.label}</div>)}</div>
          <p className="text-sm text-white/60 mt-3 flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> {r.right} of {r.answered} guessed right</p>
        </div>
      ))}
    </div>
  );
}

export function ActivityFeed({ g }) {
  const a = g.screen?.activity || [];
  if (!a.length) return null;
  return (
    <div data-testid="screen-activity">
      <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-2">Just now</p>
      {a.slice(0, 5).map((r, i) => <p key={i} className="text-white/70 text-lg flex items-center gap-2"><PlayerAvatar p={r} size={22} /> {r.name} · +{r.points}{r.label ? ` · ${r.label}` : ""}</p>)}
    </div>
  );
}
